/**
 * Receive Purchase Order drawer — PRD.211.
 *
 * Two-step drawer launched from the Delivery Orders page:
 *   Step 1: searchable Purchase Order picker (filters out / disables POs that
 *           have already been received).
 *   Step 2: receive form for the chosen PO. Renders the same shared form body
 *           used by /inventory/purchase-orders/:id/receive, so Save Draft and
 *           Receive / Finalize go through the same SQL commit path. The drawer
 *           never writes stock_movements directly — those still only come from
 *           the form's `commit(isReceive=true)` branch.
 */

import { useMemo, useState, type CSSProperties } from 'react';
import { Drawer, DrawerBadge } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qDeliveryOrdersList,
  qPurchaseOrdersList,
  type DeliveryOrderRow,
  type PurchaseOrderRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { useReceiveDeliveryOrderForm, ReceiveDeliveryOrderFormBody } from './_receive-do-form';
import { PurchaseOrderDetailsDrawer } from './PurchaseOrderDetailsDrawer';

type DeliveryStatus = 'received' | 'draft' | 'none';

interface PoCandidate {
  po: PurchaseOrderRow;
  deliveryStatus: DeliveryStatus;
}

function deliveryStatusLabel(s: DeliveryStatus): string {
  if (s === 'received') return 'Received';
  if (s === 'draft') return 'Draft DO';
  return 'Not Received';
}

export function ReceivePoDrawer({
  open,
  onOpenChange,
  brandTokens,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
}) {
  // Internal step state. Reset to 'select' whenever the drawer opens fresh.
  const [selectedPoId, setSelectedPoId] = useState<string | null>(null);
  // Stacked preview drawer over the picker.
  const [previewPoId, setPreviewPoId] = useState<string | null>(null);

  const step: 'select' | 'receive' = selectedPoId ? 'receive' : 'select';

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSelectedPoId(null);
      setPreviewPoId(null);
    }
    onOpenChange(next);
  };

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={handleOpenChange}
        portalStyle={brandTokens as CSSProperties}
        width={step === 'select' ? 'min(720px, 60vw)' : 'min(1100px, 85vw)'}
        title={step === 'select' ? 'Receive Purchase Order' : 'Receive PO'}
        // Footer is owned by each step (different actions per step).
        showCloseButton
        closeOnBackdrop
        closeOnEsc
      >
        {step === 'select' ? (
          <PoPickerStep
            onPick={(poId) => setSelectedPoId(poId)}
            onPreview={(poId) => setPreviewPoId(poId)}
            onCancel={() => handleOpenChange(false)}
          />
        ) : (
          <ReceiveFormStep
            purchaseOrderId={selectedPoId as string}
            onBack={() => setSelectedPoId(null)}
            onDone={() => handleOpenChange(false)}
          />
        )}
      </Drawer>

      <PurchaseOrderDetailsDrawer
        open={previewPoId != null}
        onOpenChange={(o) => {
          if (!o) setPreviewPoId(null);
        }}
        purchaseOrderId={previewPoId}
        brandTokens={brandTokens}
        onReceiveOverride={(poId) => {
          // From the preview, jump straight into the receive form for this PO.
          setPreviewPoId(null);
          setSelectedPoId(poId);
        }}
      />
    </>
  );
}

// ─── Step 1: PO picker ───

function PoPickerStep({
  onPick,
  onPreview,
  onCancel,
}: {
  onPick: (purchaseOrderId: string) => void;
  onPreview: (purchaseOrderId: string) => void;
  onCancel: () => void;
}) {
  const { data: poRows = [] } = usePowerSyncQuery<PurchaseOrderRow>(qPurchaseOrdersList());
  const { data: doRows = [] } = usePowerSyncQuery<DeliveryOrderRow>(qDeliveryOrdersList());

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'not_received' | 'all'>('not_received');

  // PO id → delivery status. 'received' takes precedence over 'draft'.
  const deliveryStatusByPoId = useMemo(() => {
    const map = new Map<string, DeliveryStatus>();
    for (const d of doRows) {
      if (!d.purchase_order_id) continue;
      const current = map.get(d.purchase_order_id);
      if (d.status === 'received') {
        map.set(d.purchase_order_id, 'received');
      } else if (d.status === 'draft' && current !== 'received') {
        map.set(d.purchase_order_id, 'draft');
      }
    }
    return map;
  }, [doRows]);

  const candidates: PoCandidate[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    return poRows
      .filter((po) => po.status !== 'cancelled')
      .map((po) => ({
        po,
        deliveryStatus: deliveryStatusByPoId.get(po.id) ?? 'none',
      }))
      .filter((c) => {
        if (filter === 'not_received' && c.deliveryStatus === 'received') return false;
        if (!q) return true;
        return (
          (c.po.po_number ?? '').toLowerCase().includes(q) ||
          (c.po.supplier_name ?? '').toLowerCase().includes(q) ||
          (c.po.notes ?? '').toLowerCase().includes(q) ||
          (c.po.status ?? '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        // Eligible POs first, then by po_date desc.
        const aEligible = a.deliveryStatus !== 'received' ? 0 : 1;
        const bEligible = b.deliveryStatus !== 'received' ? 0 : 1;
        if (aEligible !== bEligible) return aEligible - bEligible;
        const aDate = a.po.po_date ?? a.po.created_at ?? '';
        const bDate = b.po.po_date ?? b.po.created_at ?? '';
        return bDate.localeCompare(aDate);
      });
  }, [poRows, deliveryStatusByPoId, search, filter]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Filters */}
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-6)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          gap: 'var(--sp-3)',
          alignItems: 'center',
        }}
      >
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search PO number, supplier, notes…"
          style={{
            flex: 1,
            height: 36,
            padding: '0 12px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text)',
            outline: 'none',
            fontFamily: 'var(--font-family)',
          }}
        />
        <FilterChip
          active={filter === 'not_received'}
          onClick={() => setFilter('not_received')}
          label="Not Received"
        />
        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')} label="All" />
      </div>

      {/* Candidate list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-3) var(--sp-6)' }}>
        {candidates.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-8) var(--sp-4)',
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {filter === 'not_received'
              ? 'No purchase orders waiting to be received.'
              : 'No purchase orders match your search.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            {candidates.map((c) => (
              <PoCandidateRow
                key={c.po.id}
                candidate={c}
                onPick={() => onPick(c.po.id)}
                onPreview={() => onPreview(c.po.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          flexShrink: 0,
          padding: 'var(--sp-4) var(--sp-6)',
          borderTop: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button variant="outline" size="lg" onClick={onCancel}>
          Close
        </Button>
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        height: 32,
        padding: '0 12px',
        borderRadius: 'var(--radius-full, 999px)',
        border: `1px solid ${active ? 'var(--color-primary)' : 'var(--border)'}`,
        background: active ? 'var(--color-primary-soft, rgba(0,0,0,0.05))' : 'var(--bg-surface)',
        color: active ? 'var(--color-primary)' : 'var(--text-muted)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function PoCandidateRow({
  candidate,
  onPick,
  onPreview,
}: {
  candidate: PoCandidate;
  onPick: () => void;
  onPreview: () => void;
}) {
  const { po, deliveryStatus } = candidate;
  const isReceived = deliveryStatus === 'received';
  const fmt = useFormatMoney();

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 100px 1.2fr 110px 130px 170px',
        gap: 'var(--sp-3)',
        alignItems: 'center',
        padding: 'var(--sp-3)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: isReceived ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
        opacity: isReceived ? 0.7 : 1,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {po.po_number ?? po.id.slice(0, 8)}
        </span>
        {po.notes && (
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {po.notes}
          </span>
        )}
      </div>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
        {po.po_date ?? '—'}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {po.supplier_name ?? '—'}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {po.total_amount == null ? '—' : fmt(po.total_amount)}
      </span>
      <DrawerBadge
        label={deliveryStatusLabel(deliveryStatus)}
        variant={deliveryStatus === 'draft' ? 'warning' : 'neutral'}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
        <Button variant="outline" size="md" onClick={onPreview} title="View PO details">
          View
        </Button>
        <Button
          variant="gradient"
          size="md"
          onClick={onPick}
          disabled={isReceived}
          title={isReceived ? 'This purchase order has already been received.' : 'Receive this PO'}
        >
          Receive
        </Button>
      </div>
    </div>
  );
}

// ─── Step 2: receive form ───

function ReceiveFormStep({
  purchaseOrderId,
  onBack,
  onDone,
}: {
  purchaseOrderId: string;
  onBack: () => void;
  onDone: () => void;
}) {
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const userName = user?.displayName ?? user?.email ?? '';
  const { db } = usePowerSyncDb();

  const form = useReceiveDeliveryOrderForm({
    purchaseOrderId,
    orgId,
    userId,
    userName,
    db,
    onSaved: () => onDone(),
  });

  const disabled = form.saving !== null || form.alreadyReceived !== null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Sub-header with Back + PO context */}
      <div
        style={{
          padding: 'var(--sp-3) var(--sp-6)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
        }}
      >
        <button
          onClick={onBack}
          disabled={form.saving !== null}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            height: 32,
            padding: '0 10px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            color: 'var(--text)',
            fontSize: 'var(--font-size-sm)',
            cursor: form.saving !== null ? 'not-allowed' : 'pointer',
            opacity: form.saving !== null ? 0.6 : 1,
          }}
        >
          <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>
            ←
          </span>
          Back to POs
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Receive PO {form.po?.po_number ?? purchaseOrderId.slice(0, 8)}
          </span>
          {form.po?.supplier_name && (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              Supplier: {form.po.supplier_name}
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--sp-5) var(--sp-6)',
          background: 'var(--bg-app, #f8fafc)',
        }}
      >
        {!form.po ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
            Loading purchase order…
          </div>
        ) : (
          <ReceiveDeliveryOrderFormBody form={form} />
        )}
      </div>

      {/* Sticky footer actions */}
      <div
        style={{
          flexShrink: 0,
          padding: 'var(--sp-4) var(--sp-6)',
          borderTop: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 'var(--sp-2)',
        }}
      >
        <Button variant="outline" size="lg" onClick={onBack} disabled={form.saving !== null}>
          Cancel
        </Button>
        <Button variant="outline" size="lg" onClick={form.saveDraft} disabled={disabled}>
          {form.saving === 'draft' ? 'Saving…' : 'Save Draft'}
        </Button>
        <Button variant="gradient" size="lg" onClick={form.receive} disabled={disabled}>
          {form.saving === 'receive' ? 'Receiving…' : 'Receive / Finalize'}
        </Button>
      </div>
    </div>
  );
}
